import { onScopeDispose, readonly, ref, type Ref } from 'vue'

interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>
  readonly userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>
}

const deferredPrompt = ref<BeforeInstallPromptEvent | null>(null)
const installed = ref(false)

/** Muss früh laufen, weil `beforeinstallprompt` nur einmal feuert. */
export function initInstallPrompt(): void {
  window.addEventListener('beforeinstallprompt', (event) => {
    event.preventDefault()
    deferredPrompt.value = event as BeforeInstallPromptEvent
  })
  window.addEventListener('appinstalled', () => {
    installed.value = true
    deferredPrompt.value = null
  })
}

export function isStandalone(): boolean {
  const iosStandalone = (navigator as Navigator & { standalone?: boolean }).standalone === true
  return window.matchMedia('(display-mode: standalone)').matches || iosStandalone
}

export function isIos(): boolean {
  return /iphone|ipad|ipod/i.test(navigator.userAgent)
}

export function useInstall(): {
  canPrompt: Readonly<Ref<boolean>>
  installed: Readonly<Ref<boolean>>
  standalone: Readonly<Ref<boolean>>
  promptInstall: () => Promise<'accepted' | 'dismissed' | 'unavailable'>
} {
  const canPrompt = ref(deferredPrompt.value !== null)
  const standalone = ref(isStandalone())

  const stop = window.setInterval(() => {
    canPrompt.value = deferredPrompt.value !== null
    standalone.value = isStandalone()
  }, 1000)
  onScopeDispose(() => window.clearInterval(stop))

  async function promptInstall(): Promise<'accepted' | 'dismissed' | 'unavailable'> {
    const event = deferredPrompt.value
    if (!event) return 'unavailable'
    await event.prompt()
    const choice = await event.userChoice
    deferredPrompt.value = null
    canPrompt.value = false
    return choice.outcome
  }

  return { canPrompt: readonly(canPrompt), installed: readonly(installed), standalone: readonly(standalone), promptInstall }
}
